import { monthKey, buildMonthKeysInRange } from '../utils/dateUtils.js'

const CANONICAL_VERSION = 'indices-canon-v1'

function monthIndex(key) {
  const m = String(key || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return null
  return Number(m[1]) * 12 + Number(m[2])
}

function fromMonthIndex(idx) {
  const year = Math.floor((idx - 1) / 12)
  const month = ((idx - 1) % 12) + 1
  return `${year}-${String(month).padStart(2, '0')}`
}

function densifySerie(serie = {}) {
  const pairs = Object.entries(serie)
    .map(([k, v]) => ({ key: monthKey(k), value: Number(v) }))
    .filter((r) => r.key && Number.isFinite(r.value) && r.value > 0)
    .sort((a, b) => rCompare(a.key, b.key))
  if (!pairs.length) return { serie: {}, estimated: {} }

  const out = {}
  const estimated = {}
  pairs.forEach((p) => {
    out[p.key] = p.value
    estimated[p.key] = false
  })

  for (let i = 0; i < pairs.length - 1; i += 1) {
    const a = pairs[i]
    const b = pairs[i + 1]
    const ai = monthIndex(a.key)
    const bi = monthIndex(b.key)
    if (!ai || !bi || bi <= ai + 1) continue
    const span = bi - ai
    for (let j = 1; j < span; j += 1) {
      const key = fromMonthIndex(ai + j)
      const ratio = j / span
      out[key] = Number((a.value + (b.value - a.value) * ratio).toFixed(8))
      estimated[key] = true
    }
  }

  return { serie: out, estimated }
}

function rCompare(a, b) {
  return String(a).localeCompare(String(b))
}

function buildCoverage(serie = {}, fromKey = '2001-01', toKey = '2021-12') {
  const keys = Object.keys(serie).sort()
  const required = buildMonthKeysInRange(fromKey, toKey)
  const available = new Set(keys)
  const missing = required.filter((k) => !available.has(k))
  const missingPct = required.length > 0 ? (missing.length / required.length) * 100 : 100
  return {
    minKey: keys[0] || null,
    maxKey: keys.length ? keys[keys.length - 1] : null,
    requiredFrom: fromKey,
    requiredTo: toKey,
    requiredMonths: required.length,
    missingMonths: missing.length,
    missingPct: Number(missingPct.toFixed(2)),
    missingSample: missing.slice(0, 12),
  }
}

function hasStrictCoverage(coverage = {}) {
  const missingPct = coverage && coverage.missingPct !== undefined ? coverage.missingPct : 100
  return Number(missingPct) <= 3
}

export function buildCanonicalIndices(raw = {}) {
  const out = {}
  ;['ISBIC', 'RIPTE', 'IPC'].forEach((name) => {
    const sourceSerie = raw?.[name]?.serie || {}
    const densified = densifySerie(sourceSerie)
    const coverage = buildCoverage(densified.serie)
    out[name] = {
      fuente: raw?.[name]?.fuente || 'Serie canonica',
      serie: densified.serie,
      estimated: densified.estimated,
      coverage,
    }
  })

  const quality = summarizeQuality(out)
  const strictCoverage = {
    ISBIC: hasStrictCoverage(out?.ISBIC?.coverage),
    RIPTE: hasStrictCoverage(out?.RIPTE?.coverage),
    IPC: hasStrictCoverage(out?.IPC?.coverage),
  }
  out.__canonical = {
    version: CANONICAL_VERSION,
    builtAt: new Date().toISOString(),
    quality,
    strictCoverage,
    strictCoveragePass: strictCoverage.ISBIC && strictCoverage.RIPTE && strictCoverage.IPC,
    source: String(raw?.__source || 'unknown'),
  }
  return out
}

export function summarizeQuality(indices = {}) {
  const summary = {}
  ;['ISBIC', 'RIPTE', 'IPC'].forEach((name) => {
    const serie = indices?.[name]?.serie || {}
    const estimated = indices?.[name]?.estimated || {}
    const keys = Object.keys(serie)
    const total = keys.length
    const estimatedCount = keys.filter((k) => Boolean(estimated[k])).length
    summary[name] = {
      total,
      estimated: estimatedCount,
      estimatedPct: total > 0 ? Number(((estimatedCount / total) * 100).toFixed(2)) : 0,
    }
  })
  return summary
}
